
/** Hidden reference symbol for the list of clonable properties */
const clonablePropertiesKey = Symbol('clonable');

/** Internal interface for the clonable properties */
interface ClonablePropertyDefinition<T = any> {
    property: string;
    options: {
        method?: (item: T) => T;
    };
}

/**
 * Decorator that allows a property to be copied via the `basicClone` method
 */
/* eslint-disable @typescript-eslint/naming-convention */
export function Clonable<T = any>(options?: {method?: (item: T) => T}) {
    return (target: any, propertyKey: string) => {
        if (!target[clonablePropertiesKey]) {
            target[clonablePropertiesKey] = [];
        }
        target[clonablePropertiesKey].push({
            property: propertyKey,
            options: options
        });
    };
}

/**
 * Makes a new object of the same type as item, and copies all of the `Clonable` properties onto it
 */
export function basicClone<T>(item: T): T {
    // Get the list of properties tagged `@Clonable()`
    const clonableProperties: ClonablePropertyDefinition[] = item[clonablePropertiesKey];

    if (!clonableProperties) {
        // If no clonable properties, then just make JSON copy
        return JSON.parse(JSON.stringify(item));
    }

    // Make a new object of the same type
    const newItem = new (<any>item).constructor();

    // Copy each Clonable property onto the new object
    clonableProperties.forEach(prop => {
        newItem[prop.property] = cloneValue(item[prop.property], prop);
    });

    return newItem;
}

/**
 * Clones a single property from an object
 */
function cloneValue(originalValue: any, prop: ClonablePropertyDefinition): any {
    if (originalValue === undefined || originalValue === null) {
        // If it's undefined or null, then just use the original since they work by reference anyway
        return originalValue;
    }

    if (Array.isArray(originalValue)) {
        // If it's an array, then clone each item in the list
        return originalValue.map(value => cloneValue(value, prop));
    } else if (prop.options && prop.options.method) {
        // Call a custom method that will handle the copy process
        return prop.options.method(originalValue);
    } else if (originalValue.clone) {
        // Call clone if the property has it
        return originalValue.clone();
    } else if (originalValue instanceof Date) {
        // If it's a date, then make a new one
        return new Date(originalValue);
    }

    // Otherwise copy via JSON
    return JSON.parse(JSON.stringify(originalValue));
}
